<template>
    <div class="header-wrapper">
        <header class="header-bar">
            <div class="header-left">
                <div class="menu-btn" @click.stop="toggleMenu">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M4 6H20M4 12H20M4 18H20" stroke="#FF466A" stroke-width="2.5" stroke-linecap="round" />
                    </svg>
                </div>

                <!-- PC Dropdown for Hamburger menu -->
                <transition name="fade">
                    <div class="pc-dropdown" v-if="isMenuOpen && !isMobile" @click.stop>
                        <div class="menu-list">
                            <router-link v-for="(item, index) in menuItems" :key="index" :to="item.route" class="menu-item" @click.native="closeMenu">
                                <img v-if="item.image" src="@/assets/xigcheck.png" alt="XigCheck" class="xigcheck-logo" />
                                <span v-else>{{ item.label }}</span>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#666" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="chevron"><polyline points="9 18 15 12 9 6"></polyline></svg>
                            </router-link>
                        </div>
                    </div>
                </transition>

                <img src="@/assets/logo.png" alt="logo" class="logo" @click="$router.push('/')" />
            </div>

            <!-- Center PC Nav -->
            <div class="pc-nav" v-if="!isMobile">
                <div v-for="(nav, i) in pcNavItems" :key="i" class="pc-nav-item" @click.stop="nav.children ? togglePcDropdown(i) : (activePcDropdown = null)">
                    <router-link v-if="nav.image" :to="nav.route" class="xigcheck-link">
                        <img src="@/assets/xigcheck.png" alt="XigCheck" class="xigcheck-logo" />
                    </router-link>
                    <router-link v-else-if="!nav.children" :to="nav.route" class="pc-nav-link">{{ nav.label }}</router-link>
                    <div v-else class="pc-nav-link dropdown-trigger">{{ nav.label }}</div>

                    <transition name="fade">
                        <div class="pc-sub-dropdown" :class="{ 'xigcheck-dropdown': nav.image }" v-if="nav.children && activePcDropdown === i">
                            <component
                                v-for="(child, j) in nav.children"
                                :key="j"
                                :is="child.route ? 'router-link' : 'div'"
                                :to="child.route"
                                class="pc-sub-item"
                                :class="{ 'has-submenu': child.hasSubmenu }"
                                @click.native.stop="handlePcSubItemClick(child, j)"
                                @click.stop="handlePcSubItemClick(child, j)"
                            >
                                <span>{{ child.label }}</span>
                                <svg v-if="child.hasSubmenu" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9A9AA3" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round" class="submenu-chevron"><polyline points="9 18 15 12 9 6"></polyline></svg>
                            </component>
                            <transition name="fade">
                                <div
                                    v-if="nav.image && activePcSubmenu !== null && nav.children[activePcSubmenu] && nav.children[activePcSubmenu].children"
                                    class="pc-sub-dropdown xigcheck-side-dropdown"
                                    :style="getXigcheckSideDropdownStyle(activePcSubmenu)"
                                >
                                    <component
                                        v-for="(subItem, subIndex) in nav.children[activePcSubmenu].children"
                                        :key="subIndex"
                                        :is="subItem.route ? 'router-link' : 'div'"
                                        :to="subItem.route"
                                        class="pc-sub-item"
                                    >
                                        <span>{{ subItem.label }}</span>
                                    </component>
                                </div>
                            </transition>
                        </div>
                    </transition>
                </div>
            </div>

            <!-- Side Drawer Overlay (Mobile Only) -->
            <transition name="slide">
                <div class="mobile-drawer" v-if="isMenuOpen && isMobile">
                    <div class="drawer-header">
                        <div class="drawer-header-left">
                            <div class="menu-btn-drawer">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M4 6H20M4 12H20M4 18H20" stroke="#FF466A" stroke-width="2.5" stroke-linecap="round" />
                                </svg>
                            </div>
                            <span class="drawer-title">Help Center</span>
                        </div>
                        <div class="close-btn" @click.stop="closeMenu">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#999" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </div>
                    </div>
                    <div class="menu-list">
                        <router-link v-for="(item, index) in menuItems" :key="index" :to="item.route" class="menu-item" @click.native="closeMenu">
                            <img v-if="item.image" src="@/assets/xigcheck.png" alt="XigCheck" class="xigcheck-logo" />
                            <span v-else>{{ item.label }}</span>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#666" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="chevron"><polyline points="9 18 15 12 9 6"></polyline></svg>
                        </router-link>
                    </div>
                </div>
            </transition>
            <transition name="fade">
                <div class="mobile-overlay" v-if="isMenuOpen && isMobile" @click.stop="closeMenu"></div>
            </transition>

            <div class="header-right">
                <template v-if="isMobile">
                    <a href="https://x.com/xigfor?s=21" target="_blank" rel="noopener" class="icon-link" aria-label="Twitter"><img src="@/assets/X.png" alt="Twitter" class="social-icon" /></a>
                    <a href="https://www.facebook.com/share/1C1H5yGgGQ/?mibextid=wwXIfr" target="_blank" rel="noopener" class="icon-link" aria-label="Facebook"><img src="@/assets/facebook.png" alt="Facebook" class="social-icon" /></a>
                    <a href="https://www.reddit.com/u/XigFor/s/Uw8UQnBWbe" target="_blank" rel="noopener" class="icon-link" aria-label="Reddit"><img src="@/assets/reddit.png" alt="Reddit" class="social-icon" /></a>
                </template>

                <template v-if="isLoggedIn">
                    <div ref="userMenu" class="user-menu-wrap">
                        <button type="button" class="user-trigger" @click.stop="toggleUserMenu" :data-tooltip="displayName">
                            <div class="user-avatar-wrap">
                                <img src="@/assets/logo_only.png" alt="User" class="user-avatar user-avatar-fallback" />
                            </div>
                            <span class="user-display-name">{{ displayName }}</span>
                            <svg class="user-caret" width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M1 1.5L6 6.5L11 1.5" stroke="#B2A7A7" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                        </button>
                        <transition name="fade">
                            <div v-if="isUserMenuOpen" class="user-dropdown">
                                <button type="button" class="logout-btn" @click="logout">Log out</button>
                            </div>
                        </transition>
                    </div>
                </template>
                <template v-else>
                    <router-link to="/signin" class="signin-btn" :class="{ 'pc-text-btn': !isMobile }">Login</router-link>
                    <router-link to="/signup" class="signup-btn">Sign Up</router-link>
                </template>
            </div>
        </header>
    </div>
</template>

<script>
export default {
    name: 'Header',
    data() {
        return {
            isMenuOpen: false,
            isUserMenuOpen: false,
            isMobile: false,
            authToken: '',
            userProfile: null,
            activePcDropdown: null,
            activePcSubmenu: null,
            menuItems: [
                { label: 'XigCheck', route: '/xigcheck', image: true },
                { label: 'Service Fees & Charges', route: '/info/service-fees' },
                { label: 'Programs & Benefits', route: '/info/participation-benefits' },
                { label: 'Platform Compliance & Safety', route: '/info/compliance-standards' },
                { label: 'privacy', route: '/info/privacy-data' },
                { label: 'Legal', route: '/info/legal-notices' }
            ],
            pcNavItems: [
                { label: 'Home', route: '/' },
                {
                    label: 'User Rewards',
                    children: [
                        { label: 'Growth Program Overview', route: '/content/growth-program' },
                        { label: 'SmartRewards Rules & Terms', route: '/content/smartrewards-rules' },
                        { label: 'SmartRewards Terms & Conditions', route: '/content/smartrewards-terms' }
                    ]
                },
                {
                    label: 'Compliance',
                    children: [
                        { label: 'Compliance Standards & User Protection', route: '/info/compliance-standards' },
                        { label: 'Platform Disclaimer', route: '/content/platform-disclaimer' }
                    ]
                },
                {
                    label: 'Legal',
                    children: [
                        { label: 'Terms of Service', route: '/content/terms-of-service' },
                        { label: 'Privacy Policy', route: '/content/privacy-policy' },
                        { label: 'Intellectual Property Policy', route: '/content/intellectual-property' }
                    ]
                },
                {
                    image: true,
                    route: '/xigcheck'
                }
            ]
        }
    },
    computed: {
        isLoggedIn() {
            return !!this.authToken
        },
        displayName() {
            const profile = this.userProfile || {}
            return profile.nickname || profile.username || profile.name || 'xig123456'
        }
    },
    watch: {
        $route() {
            this.syncAuthState()
            this.isUserMenuOpen = false
            this.isMenuOpen = false
            this.activePcDropdown = null
            this.activePcSubmenu = null
        }
    },
    mounted() {
        this.syncAuthState()
        this.checkMobile()
        window.addEventListener('resize', this.checkMobile)
        document.addEventListener('click', this.handleClickOutside)
        window.addEventListener('storage', this.handleStorageChange)
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.checkMobile)
        document.removeEventListener('click', this.handleClickOutside)
        window.removeEventListener('storage', this.handleStorageChange)
    },
    methods: {
        syncAuthState() {
            this.authToken = localStorage.getItem('token') || ''
            this.loadUserProfile()
        },
        loadUserProfile() {
            const possibleKeys = ['userInfo', 'user', 'memberInfo', 'member', 'profile']
            for (const key of possibleKeys) {
                const raw = localStorage.getItem(key)
                if (!raw) continue
                try {
                    const parsed = JSON.parse(raw)
                    if (parsed && typeof parsed === 'object') {
                        this.userProfile = parsed
                        return
                    }
                } catch (error) {
                    console.warn(`Failed to parse ${key} from localStorage`, error)
                }
            }
            this.userProfile = null
        },
        checkMobile() {
            const isCurrentlyMobile = window.innerWidth <= 900
            if (this.isMobile !== isCurrentlyMobile) {
                this.isMobile = isCurrentlyMobile
                if (this.isMenuOpen) this.isMenuOpen = false
                if (this.isUserMenuOpen) this.isUserMenuOpen = false
            }
        },
        toggleMenu() {
            this.isMenuOpen = !this.isMenuOpen
            if (this.isMenuOpen) this.isUserMenuOpen = false
        },
        closeMenu() {
            this.isMenuOpen = false
        },
        togglePcDropdown(index) {
            if (this.activePcDropdown === index) {
                this.activePcDropdown = null
                this.activePcSubmenu = null
            } else {
                this.activePcDropdown = index
                this.activePcSubmenu = null
            }
        },
        handlePcSubItemClick(child, index) {
            if (child.hasSubmenu) {
                this.activePcSubmenu = this.activePcSubmenu === index ? null : index
            } else {
                this.activePcSubmenu = null
            }
        },
        getXigcheckSideDropdownStyle(index) {
            const itemHeights = [69, 48, 48, 69, 48, 48]
            const dropdownPaddingTop = 12
            const top = itemHeights.slice(0, index).reduce((sum, height) => sum + height, dropdownPaddingTop)
            return { top: `${top}px` }
        },
        toggleUserMenu() {
            this.isUserMenuOpen = !this.isUserMenuOpen
            if (this.isUserMenuOpen) this.isMenuOpen = false
        },
        logout() {
            localStorage.removeItem('token')
            localStorage.removeItem('userInfo')
            localStorage.removeItem('user')
            localStorage.removeItem('memberInfo')
            localStorage.removeItem('member')
            localStorage.removeItem('profile')
            this.authToken = ''
            this.userProfile = null
            this.isUserMenuOpen = false
            if (this.$route.path !== '/') {
                this.$router.push('/')
                return
            }
        },
        handleStorageChange() {
            this.syncAuthState()
        },
        handleClickOutside(event) {
            if (this.isMenuOpen && !this.isMobile) {
                this.isMenuOpen = false
            }
            if (this.isUserMenuOpen) {
                const userMenu = this.$refs.userMenu
                if (userMenu && !userMenu.contains(event.target)) {
                    this.isUserMenuOpen = false
                }
            }
            if (this.activePcDropdown !== null) {
                this.activePcDropdown = null
                this.activePcSubmenu = null
            }
        }
    }
}
</script>

<style scoped>
.header-wrapper {
    background: #fff;
    border-bottom: 2px solid #f3f3f3;
    position: relative;
    z-index: 100;
}
.header-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 18px;
    /* background: #fff; */
    max-width: 1300px;
    margin: 0 auto;
}

.header-left {
    display: flex;
    align-items: center;
    margin-left: 2%;
    position: relative;
    z-index: 10;
}
.menu-btn {
    cursor: pointer;
    margin-right: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.2s;
}
.menu-btn svg {
    width: 24px;
    height: 24px;
}
.menu-btn:hover {
    opacity: 0.8;
}
.logo {
    height: 30px;
    border-radius: 6px;
    margin-right: 8px;
    cursor: pointer;
}

/* PC Hamburger Dropdown */
.pc-dropdown {
    position: absolute;
    top: calc(100% + 14px);
    left: -8px;
    background: white;
    min-width: 250px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    border-radius: 6px;
    z-index: 200;
    padding: 8px 0;
}

/* PC Center Nav */
.pc-nav {
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: absolute;
    left: 50%;
    width: 58%;
    max-width: 760px;
    transform: translateX(-50%);
    height: 100%;
}
.pc-nav-item {
    position: relative;
    height: 100%;
    display: flex;
    align-items: center;
    cursor: pointer;
}
.pc-nav-link {
    font-size: 15px;
    font-weight: 500;
    color: #999;
    text-decoration: none;
    transition: color 0.2s;
    padding: 24px 0;
}
.pc-nav-item:hover .pc-nav-link {
    color: #222;
}
.dropdown-trigger {
    cursor: pointer;
}
.pc-sub-dropdown {
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    background: #fff;
    min-width: 280px;
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
    display: flex;
    flex-direction: column;
    padding: 12px 0;
    z-index: 200;
}
.xigcheck-dropdown {
    min-width: 280px;
    width: 320px;
}
.xigcheck-side-dropdown {
    left: auto;
    right: 100%;
    top: 0;
    transform: none;
    width: 320px;
}
.pc-sub-item {
    padding: 14px 24px;
    color: #888;
    font-size: 14px;
    text-decoration: none;
    white-space: nowrap;
    transition: color 0.2s;
}
.xigcheck-dropdown .pc-sub-item {
    align-items: center;
    display: flex;
    justify-content: space-between;
    line-height: 1.45;
    white-space: normal;
}
.pc-sub-item:hover {
    color: #222;
}
.submenu-chevron {
    flex-shrink: 0;
    margin-left: 18px;
}

/* Base Right Styles */
.header-right {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-right: 2%;
    z-index: 10;
}
.xigcheck-logo {
    width: 99px;
    height: auto;
    object-fit: contain;
}
.xigcheck-link {
    display: flex;
    align-items: center;
    text-decoration: none;
}
.signup-btn {
    background: #ff6b6b;
    color: white;
    padding: 8px 18px;
    border-radius: 6px;
    text-decoration: none;
    font-weight: 500;
    font-size: 14px;
    transition: background-color 0.3s;
}
.signup-btn:hover {
    background: #ff5252;
}
.signin-btn {
    background: white;
    color: #666;
    padding: 6px 14px;
    border-radius: 6px;
    text-decoration: none;
    font-weight: 500;
    font-size: 13px;
    border: 1px solid #ddd;
    transition: all 0.3s;
}
.signin-btn:hover {
    background: #f5f5f5;
    border-color: #ccc;
}
.pc-text-btn {
    background: transparent !important;
    border: none !important;
    color: #999 !important;
    font-size: 15px !important;
    padding: 0 !important;
}
.pc-text-btn:hover {
    color: #333 !important;
    background: transparent !important;
}

.icon-link {
    display: flex;
    align-items: center;
    transition: transform 0.15s;
    cursor: pointer;
}
.icon-link:hover {
    transform: scale(1.15);
}
.social-icon {
    height: 24px;
    width: 24px;
    object-fit: contain;
}
.user-menu-wrap {
    position: relative;
    display: flex;
    align-items: center;
}
.user-trigger {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 0;
    background: transparent;
    border: none;
    cursor: pointer;
}
.user-avatar-wrap {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
}
.user-avatar {
    width: 100%;
    height: 100%;
    object-fit: cover;
}
.user-avatar-fallback {
    object-fit: contain;
}
.user-display-name {
    max-width: 200px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    color: #9f9494;
    font-size: 15px;
    font-weight: 500;
}

/* Custom Tooltip */
.user-trigger {
    position: relative;
}
.user-trigger::after {
    content: attr(data-tooltip);
    position: absolute;
    top: calc(100% + 10px);
    left: 50%;
    transform: translateX(-50%) translateY(-5px);
    background: #474747;
    color: #fff;
    padding: 6px 12px;
    border-radius: 8px;
    font-size: 13px;
    white-space: nowrap;
    opacity: 0;
    visibility: hidden;
    transition: all 0.2s ease;
    pointer-events: none;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 1000;
}
.user-trigger:hover::after {
    opacity: 1;
    visibility: visible;
    transform: translateX(-50%) translateY(0);
}
/* Small arrow for tooltip */
.user-trigger::before {
    content: "";
    position: absolute;
    top: calc(100% + 4px);
    left: 50%;
    transform: translateX(-50%) translateY(-5px);
    border: 6px solid transparent;
    border-bottom-color: #474747;
    opacity: 0;
    visibility: hidden;
    transition: all 0.2s ease;
    pointer-events: none;
    z-index: 1000;
}
.user-trigger:hover::before {
    opacity: 1;
    visibility: visible;
    transform: translateX(-50%) translateY(0);
}
.user-caret {
    flex-shrink: 0;
    transition: transform 0.2s ease;
}
.user-menu-wrap:hover .user-caret,
.user-trigger:focus .user-caret {
    transform: translateY(1px);
}
.user-dropdown {
    position: absolute;
    top: calc(100% + 14px);
    right: 0;
    min-width: 112px;
    padding: 12px;
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 14px 32px rgba(71, 47, 47, 0.16);
    z-index: 120;
}
.logout-btn {
    width: 100%;
    border: none;
    border-radius: 14px;
    background: #fff;
    color: #232323;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
}

/* Side Drawer Overlay */
.mobile-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 999;
}
.mobile-drawer {
    position: fixed;
    top: 0;
    left: 0;
    width: 280px;
    height: 100vh;
    background: white;
    z-index: 1000;
    display: flex;
    flex-direction: column;
    box-shadow: 2px 0 16px rgba(0, 0, 0, 0.08);
}
.drawer-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    border-bottom: 2px solid #f3f3f3;
}
.drawer-header-left {
    display: flex;
    align-items: center;
}
.menu-btn-drawer {
    margin-right: 12px;
    display: flex;
}
.drawer-title {
    color: #ff466a;
    font-size: 18px;
    font-weight: 700;
}
.close-btn {
    cursor: pointer;
    display: flex;
}
.close-btn svg {
    stroke: #999;
}

/* PC Override Drawer Styling */
.menu-list {
    display: flex;
    flex-direction: column;
}
.menu-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    font-size: 14px;
    color: #555;
    cursor: pointer;
    font-weight: 500;
    transition: background 0.2s;
    text-decoration: none;
}
.menu-item:hover {
    background: #f9f9f9;
    color: #333;
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s;
}
.fade-enter,
.fade-leave-to {
    opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
    transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}
.slide-enter,
.slide-leave-to {
    transform: translateX(-100%);
}

@media (max-width: 900px) {
    .header-bar {
        padding: 16px 0 8px 0;
        margin: 0;
    }
    .header-left {
        margin-left: 12px;
    }
    .menu-btn {
        margin-right: 12px;
    }
    .header-right {
        margin-right: 12px;
        gap: 8px;
    }
    .logo {
        width: 68px;
        height: 22px;
    }
    .social-icon {
        height: 17px;
        width: 17px;
    }
    .user-trigger {
        gap: 6px;
    }
    .user-avatar-wrap {
        width: 24px;
        height: 24px;
        border-radius: 7px;
    }
    .user-display-name {
        max-width: 72px;
        font-size: 12px;
    }
    .logout-btn {
        font-size: 14px;
    }
    .signup-btn,
    .signin-btn {
        padding: 6px 12px;
        font-size: 10px;
    }
    .pc-nav {
        display: none;
    }
}

@media (min-width: 901px) and (max-width: 1100px) {
    .pc-nav {
        width: 60%;
    }
    .xigcheck-logo {
        width: 86px;
    }
}
</style>
